import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Sparkles, Menu, X } from "lucide-react";
import { HERO } from "@/constants/testIds";
import { MagneticButton } from "@/components/common/MagneticButton";
import { scrollToSection } from "@/components/common/DeferredMount";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  const links = [
    { label: "Showcase", href: "#showcase", testid: HERO.navLinkShowcase },
    { label: "Results", href: "#results", testid: HERO.navLinkResults },
    { label: "Why JWS", href: "#features", testid: HERO.navLinkFeatures },
  ];

  const handleNavClick = (e, id) => {
    e.preventDefault();
    scrollToSection(id);
  };

  return (
    <motion.header
      data-testid={HERO.navRoot}
      initial={{ y: -30, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled || mobileOpen ? "glass-panel shadow-lg shadow-black/40" : "bg-transparent border-b border-transparent"
      }`}
    >
      <nav className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-10">
        <a
          href="#home"
          data-testid={HERO.navLogo}
          className="group flex items-center gap-2.5"
        >
          <motion.span
            initial={{ rotate: -20, scale: 0.6, opacity: 0 }}
            animate={{ rotate: 0, scale: 1, opacity: 1 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
            className="relative flex h-9 w-9 items-center justify-center rounded-xl bg-brand-gradient shadow-glow-primary"
          >
            <Sparkles className="h-4.5 w-4.5 text-white" strokeWidth={2} />
            <span className="absolute inset-0 rounded-xl bg-brand-gradient blur-md opacity-60 animate-pulse-glow" />
          </motion.span>
          <span className="font-display text-lg font-semibold tracking-tight text-white">
            JWS<span className="text-white/50 font-body font-normal text-sm ml-1.5 hidden sm:inline">Digital Solutions</span>
          </span>
        </a>

        <div className="hidden items-center gap-9 md:flex">
          {links.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={(e) => handleNavClick(e, link.href.slice(1))}
              data-testid={link.testid}
              className="text-sm font-medium text-white/72 transition-colors hover:text-white"
            >
              {link.label}
            </a>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <MagneticButton strength={0.3} className="hidden sm:block">
            <a
              href="#cta"
              onClick={(e) => handleNavClick(e, "cta")}
              data-testid={HERO.navCta}
              className="group inline-flex items-center gap-2 rounded-full bg-brand-gradient px-5 py-2.5 text-sm font-semibold text-white shadow-glow-primary transition-[transform,box-shadow] duration-200 hover:scale-[1.03] hover:shadow-glow-primary-lg active:scale-[0.98]"
            >
              Book a Call
              <ArrowRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-1" />
            </a>
          </MagneticButton>

          <button
            type="button"
            data-testid={HERO.navMobileToggle}
            aria-label={mobileOpen ? "Close navigation menu" : "Open navigation menu"}
            aria-expanded={mobileOpen}
            aria-controls={HERO.navMobileMenu}
            onClick={() => setMobileOpen((v) => !v)}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-white/[0.08] bg-white/[0.04] text-white transition-colors hover:bg-white/[0.08] md:hidden"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            id={HERO.navMobileMenu}
            data-testid={HERO.navMobileMenu}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden border-t border-white/[0.08] md:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-4">
              {links.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  data-testid={`${link.testid}-mobile`}
                  onClick={(e) => {
                    handleNavClick(e, link.href.slice(1));
                    setMobileOpen(false);
                  }}
                  className="rounded-lg px-3 py-3 text-base font-medium text-white/80 transition-colors hover:bg-white/[0.06] hover:text-white"
                >
                  {link.label}
                </a>
              ))}
              <a
                href="#cta"
                data-testid={`${HERO.navCta}-mobile`}
                onClick={(e) => {
                  handleNavClick(e, "cta");
                  setMobileOpen(false);
                }}
                className="mt-2 flex items-center justify-center gap-2 rounded-full bg-brand-gradient px-5 py-3 text-sm font-semibold text-white shadow-glow-primary"
              >
                Book a Call
                <ArrowRight className="h-3.5 w-3.5" />
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
